import React, { useMemo, useState, useEffect } from 'react';
import Modal from '../common/modal';
import { Tag } from '../../hooks/useTags';
import { ListenerAudio } from '../../hooks/useListenerAudio';
import { Resource } from '../../hooks/useResources';
import useAuth from '../../hooks/useAuth';

interface ContentDetailsModalProps {
  title: string;
  data: ListenerAudio | Resource;
  onClose: () => void;
  tagMap?: Map<number, Tag>;
  onSaveListenerAudio?: (payload: { ai_title?: string; ai_summary?: string }) => void;
}

const OMITTED_FIELDS = new Set(['listener_role', 'listener_role_id']);
const TAG_ID_KEYS = new Set(['topic_tag_ids', 'tag_ids']);
const EDITABLE_AI_FIELDS = new Set(['ai_title', 'ai_summary']);

/** Any property value when iterating ListenerAudio or Resource entries */
type ContentDetailValue = ListenerAudio[keyof ListenerAudio] | Resource[keyof Resource];

const formatLabel = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const sanitizeFilename = (filename: string): string =>
  filename
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_{2,}/g, '_')
    .trim();

const getFileExtension = (fileUrl: string, mediaType: string | null): string => {
  const urlMatch = fileUrl.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
  if (urlMatch) return urlMatch[1];
  return mediaType?.toUpperCase() === 'VIDEO' ? 'mp4' : 'mp3';
};

const formatValue = (key: string, value: ContentDetailValue, tagMap?: Map<number, Tag>) => {
  if (value === null || value === undefined) {
    return '—';
  }

  if (TAG_ID_KEYS.has(key) && Array.isArray(value)) {
    const tagNames = value
      .map((id) => {
        if (typeof id === 'number') {
          return tagMap?.get(id)?.name ?? id.toString();
        }
        return String(id);
      })
      .filter(Boolean);

    return tagNames.length ? tagNames.join(', ') : '—';
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return '—';
    }
    return value
      .map((item) => {
        if (item === null || item === undefined) {
          return '—';
        }
        if (typeof item === 'object') {
          return JSON.stringify(item);
        }
        return String(item);
      })
      .join(', ');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
};

const getEntries = (data: ListenerAudio | Resource, tagMap?: Map<number, Tag>): [string, string][] => {
  if (!data) return [];

  const entries = Object.entries(data) as [string, ContentDetailValue][];
  return entries
    .filter(([key]) => !OMITTED_FIELDS.has(key))
    .map(([key, value]) => [key, formatValue(key, value, tagMap)]);
};

const ContentDetailsModal: React.FC<ContentDetailsModalProps> = ({
  title,
  data,
  onClose,
  tagMap,
  onSaveListenerAudio,
}) => {
  const entries = useMemo(() => getEntries(data, tagMap), [data, tagMap]);
  const { authToken } = useAuth();

  const isListenerAudio = (d: ListenerAudio | Resource): d is ListenerAudio => 'listener_role_id' in d;

  const showEditableAiSection = isListenerAudio(data) && onSaveListenerAudio != null;
  const [aiTitle, setAiTitle] = useState('');
  const [aiSummary, setAiSummary] = useState('');

  useEffect(() => {
    if (isListenerAudio(data)) {
      setAiTitle(data.ai_title ?? '');
      setAiSummary(data.ai_summary ?? '');
    }
  }, [data]);

  const displayEntries = useMemo(
    () => (showEditableAiSection ? entries.filter(([key]) => !EDITABLE_AI_FIELDS.has(key)) : entries),
    [entries, showEditableAiSection],
  );

  const handleSaveAiFields = () => {
    if (!onSaveListenerAudio) return;
    onSaveListenerAudio({
      ai_title: aiTitle.trim() || undefined,
      ai_summary: aiSummary.trim() || undefined,
    });
  };

  const handleDownload = async () => {
    if (!isListenerAudio(data) || !data.file_url) {
      return;
    }

    const mediaType = data.media_type;
    const isVideo = mediaType?.toUpperCase() === 'VIDEO';
    const fileUrl = data.file_url;

    const urlParts = fileUrl.split('/');
    const filenameFromUrl = urlParts[urlParts.length - 1]?.split('?')[0] || 'file';
    const baseFilename = data.ai_title || filenameFromUrl;
    const sanitizedFilename = sanitizeFilename(baseFilename);
    const extension = getFileExtension(fileUrl, mediaType);
    const downloadFilename = `${sanitizedFilename}.${extension}`;

    try {
      const domain = process.env.REACT_APP_DOMAIN_NAME || window.location.origin;
      const endpointPath = isVideo ? `/video/${filenameFromUrl}` : `/audio/${filenameFromUrl}`;
      const fullUrl = `${domain}${endpointPath}`;

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const showDownloadButton = isListenerAudio(data) && data.file_url;
  const downloadLabel = isListenerAudio(data) && data.media_type?.toUpperCase() === 'VIDEO' ? 'Video' : 'Audio';

  return (
    <Modal
      isModel
      InnerComponent={
        <div
          onClick={(event) => event.stopPropagation()}
          className="inline-block w-full transform overflow-hidden bg-white px-6 pb-6 pt-6 align-bottom shadow-xl transition-all sm:my-8 sm:max-w-4xl sm:align-middle"
        >
          <div className="flex items-center justify-between pb-4">
            <h3 className="blue-primary text-2xl font-normal">{title}</h3>
            <div className="flex items-center gap-3">
              {showDownloadButton && (
                <button
                  className="bg-blue-primary rounded-md px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  onClick={handleDownload}
                >
                  Download {downloadLabel}
                </button>
              )}
              <button className="text-blue-primary text-sm font-semibold" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
            {showEditableAiSection && (
              <div className="mb-4 rounded-md bg-gray-100 p-4 text-sm">
                <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Editable</div>
                <div className="grid gap-3 sm:grid-cols-1">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      AI title
                    </label>
                    <input
                      type="text"
                      value={aiTitle}
                      onChange={(e) => setAiTitle(e.target.value)}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-gray-800"
                      placeholder="AI-generated title"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      AI summary
                    </label>
                    <textarea
                      value={aiSummary}
                      onChange={(e) => setAiSummary(e.target.value)}
                      rows={4}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-gray-800"
                      placeholder="AI-generated summary"
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      className="bg-blue-primary rounded-md px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                      onClick={handleSaveAiFields}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
            <dl className="grid grid-cols-[minmax(0,160px)_minmax(0,1fr)] gap-x-4 gap-y-3 rounded-md bg-gray-100 p-4 text-sm">
              {displayEntries.map(([key, value]) => (
                <div key={key} style={{ display: 'contents' }}>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{formatLabel(key)}</dt>
                  <dd className="whitespace-pre-wrap break-words text-gray-800">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      }
    />
  );
};

export default ContentDetailsModal;
